import React from "react";
import { Stack, Text, TextLink } from "@origin-digital/ods-core";
import { useAppContext } from "../../context/AppContext";
import { useOffercode } from "../../hooks/useOffercode";
import { ILPGOffer } from "../../model/ILPGOffer";
import { OfferType } from "../Offers/offerTab";
import {
  STANDARD_EXCHANGE_OFFER_DISCLAIMERS,
  STANDARD_REFILL_OFFER_DISCLAIMERS,
  DISCOUNTED_EXCHANGE_OFFER_DISCLAIMERS,
  DISCOUNTED_REFILL_OFFER_DISCLAIMERS,
  LPGBUNDLE_EXCHANGE_DISCLAIMERS,
  LPGBUNDLE_REFILL_DISCLAIMERS,
  LPGMOVEOFFERS_EXCHANGE_DISCLAIMERS,
  LPGMOVEOFFERS_REFILL_DISCLAIMERS,
} from "../OfferDisclaimer/OfferDisclaimer";

const OFFER_CODE_DISCLAIMER_MAP: {
  [key: string]: { exchange: string[]; refill: string[] };
} = {
  lpgbundleoffer: {
    exchange: LPGBUNDLE_EXCHANGE_DISCLAIMERS,
    refill: LPGBUNDLE_REFILL_DISCLAIMERS,
  },
  lpgmoveoffer: {
    exchange: LPGMOVEOFFERS_EXCHANGE_DISCLAIMERS,
    refill: LPGMOVEOFFERS_REFILL_DISCLAIMERS,
  },
  movers50: {
    exchange: LPGMOVEOFFERS_EXCHANGE_DISCLAIMERS,
    refill: LPGMOVEOFFERS_REFILL_DISCLAIMERS,
  },
};

const LPG_STANDARD_OFFER_NAME = "LPG Standard";
const LPG_STANDARD_TC_URL =
  "https://www.originenergy.com.au/for-home/lpg/lpg-plans/termsconditionsoffers/";

type OfferTermsSectionProps = {
  offerType: OfferType;
  selectedOffer?: ILPGOffer;
};

export const OfferTermsSection = ({
  offerType,
  selectedOffer,
}: OfferTermsSectionProps) => {
  const { context } = useAppContext();
  const { loading } = context;
  const { offers } = { ...context?.offersData };

  const termsAndConditionsLink =
    selectedOffer?.offerName === LPG_STANDARD_OFFER_NAME
      ? LPG_STANDARD_TC_URL
      : selectedOffer?.termsAndConditionsLink ?? LPG_STANDARD_TC_URL;

  const offerCode = useOffercode()?.toLocaleLowerCase();
  const offerCodeMap = offerCode ? OFFER_CODE_DISCLAIMER_MAP[offerCode] : null;
  const hasUnknownOfferCode = !!offerCode && !offerCodeMap;

  const offerToCheck =
    selectedOffer ??
    offers?.find((offer) => {
      const isRefill = offerType === OfferType.REFILL;
      return offer.isRefill === isRefill;
    });
  const isDiscounted =
    !offerCode &&
    !!offerToCheck?.bottlePricing?.["1"] &&
    Number(offerToCheck.bottlePricing["1"].listPrice) >
      Number(offerToCheck.bottlePricing["1"].price);

  const disclaimers: (string | React.JSX.Element)[] = offerCodeMap
    ? offerType === OfferType.REFILL
      ? offerCodeMap.refill
      : offerCodeMap.exchange
    : hasUnknownOfferCode
    ? offerType === OfferType.REFILL
      ? LPGMOVEOFFERS_REFILL_DISCLAIMERS
      : LPGMOVEOFFERS_EXCHANGE_DISCLAIMERS
    : isDiscounted
    ? offerType === OfferType.REFILL
      ? DISCOUNTED_REFILL_OFFER_DISCLAIMERS
      : DISCOUNTED_EXCHANGE_OFFER_DISCLAIMERS
    : offerType === OfferType.REFILL
    ? STANDARD_REFILL_OFFER_DISCLAIMERS
    : STANDARD_EXCHANGE_OFFER_DISCLAIMERS;

  const isPriceInfoAvailableForOffer = offers?.some((offer) => {
    const isRefill = offerType === OfferType.REFILL;
    return (
      offer.isRefill === isRefill &&
      offer.bottlePricing?.["1"] &&
      Number(offer.bottlePricing?.["1"].price) > 0
    );
  });

  if (offers?.length === 0 || loading) {
    return null;
  }

  return (
    <Stack space="small" data-id="offer-terms-section">
      <Text tone="neutral" variant="body">
        <TextLink
          href={termsAndConditionsLink}
          target="_blank"
          data-id="offer-terms-link"
        >
          Offer Terms and Conditions
        </TextLink>
      </Text>
      <Text tone="neutral" variant="body">
        <TextLink
          href="/wp-content/uploads/lpg-customer-charter-10732.pdf"
          target="_blank"
          data-id="customer-charter-link"
        >
          Customer Charter &ndash; Agreement Terms
        </TextLink>
      </Text>

      {isPriceInfoAvailableForOffer &&
        disclaimers.map((disclaimer, index) => (
          <Text
            key={index}
            variant="body-small"
            tone="neutral"
            data-id={`offer-terms-disclaimer-${index}`}
          >
            {disclaimer}
          </Text>
        ))}
    </Stack>
  );
};
